import React, { useState } from 'react';
import { 
  Shield, 
  User, 
  Mail, 
  Building2, 
  CheckCircle, 
  AlertTriangle,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { useMutation, useQueryClient } from 'react-query';
import { apiClient } from '../lib/api';
import toast from 'react-hot-toast';

interface RegistrationData {
  schoolName: string;
  contactName: string;
  contactEmail: string;
  additionalInfo?: string;
}

interface RegistrationResponse {
  success: boolean;
  message: string;
  data?: {
    installationId: string;
    licenseKey: string;
    licenseType: string;
    expiresAt: string;
    features: any;
    maxUsers: number;
    maxInstallations: number;
  };
}

interface LicenseRegistrationProps {
  onRegistrationComplete?: (data: any) => void;
  onCancel?: () => void;
}

export default function LicenseRegistration({ onRegistrationComplete, onCancel }: LicenseRegistrationProps) {
  const [formData, setFormData] = useState<RegistrationData>({
    schoolName: '',
    contactName: '',
    contactEmail: '',
    additionalInfo: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const registrationMutation = useMutation(
    async (data: RegistrationData): Promise<RegistrationResponse> => {
      const response = await apiClient.post('/license/register', data);
      return response.data;
    },
    {
      onSuccess: (data) => {
        if (data.success) {
          toast.success('Installation erfolgreich registriert!');
          queryClient.invalidateQueries('licenseStatus');
          if (onRegistrationComplete) {
            onRegistrationComplete(data.data);
          }
        } else {
          toast.error(data.message || 'Registrierung fehlgeschlagen');
        }
      },
      onError: (error: any) => {
        console.error('❌ Registrierungsfehler:', error);
        toast.error('Fehler bei der Registrierung. Bitte versuchen Sie es erneut.');
      }
    }
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validierung
    if (!formData.schoolName.trim()) {
      toast.error('Schulname ist erforderlich');
      return;
    }
    
    if (!formData.contactName.trim()) {
      toast.error('Kontaktperson ist erforderlich');
      return;
    }
    
    if (!formData.contactEmail.trim() || !formData.contactEmail.includes('@')) {
      toast.error('Gültige E-Mail-Adresse ist erforderlich');
      return;
    }

    setIsSubmitting(true);
    try {
      await registrationMutation.mutateAsync(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof RegistrationData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg border">
      <div className="text-center mb-6">
        <div className="flex justify-center mb-4">
          <Shield className="w-12 h-12 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Lernrakete Installation registrieren
        </h2>
        <p className="text-gray-600">
          Registrieren Sie Ihre Lernrakete-Installation, um eine kostenlose Trial-Lizenz zu erhalten.
        </p>
      </div>

      {/* Trial-Lizenz Info */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-900 mb-2">Kostenlose Trial-Lizenz</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• 30 Tage kostenlose Nutzung</li>
              <li>• Alle Grundfunktionen verfügbar</li>
              <li>• AI-Assistant inklusive</li>
              <li>• Bis zu 10 Benutzer</li>
              <li>• Vollständiger Support</li>
            </ul>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Schulname */}
        <div>
          <label htmlFor="schoolName" className="block text-sm font-medium text-gray-700 mb-2">
            <Building2 className="w-4 h-4 inline mr-2" />
            Schulname *
          </label>
          <input
            type="text"
            id="schoolName"
            value={formData.schoolName}
            onChange={(e) => handleInputChange('schoolName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="z.B. Grundschule Musterstadt"
            required
          />
        </div>

        {/* Kontaktperson */}
        <div>
          <label htmlFor="contactName" className="block text-sm font-medium text-gray-700 mb-2">
            <User className="w-4 h-4 inline mr-2" />
            Kontaktperson *
          </label>
          <input
            type="text"
            id="contactName"
            value={formData.contactName}
            onChange={(e) => handleInputChange('contactName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="z.B. Max Mustermann"
            required
          />
        </div>

        {/* E-Mail */}
        <div>
          <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700 mb-2">
            <Mail className="w-4 h-4 inline mr-2" />
            E-Mail-Adresse *
          </label>
          <input
            type="email"
            id="contactEmail"
            value={formData.contactEmail}
            onChange={(e) => handleInputChange('contactEmail', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="kontakt@schule.de"
            required
          />
          <p className="mt-1 text-xs text-gray-500">
            Diese E-Mail wird für Lizenz-Benachrichtigungen verwendet
          </p>
        </div>

        {/* Zusätzliche Informationen */}
        <div>
          <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-2">
            Zusätzliche Informationen (optional)
          </label>
          <textarea
            id="additionalInfo"
            value={formData.additionalInfo}
            onChange={(e) => handleInputChange('additionalInfo', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="z.B. Anzahl der geplanten Benutzer, spezielle Anforderungen..."
          />
        </div>

        {/* Datenschutz-Hinweis */}
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-start space-x-2">
            <AlertTriangle className="w-4 h-4 text-gray-600 mt-0.5" />
            <div className="text-sm text-gray-700">
              <p className="font-medium mb-1">Datenschutz-Hinweis</p>
              <p>
                Ihre Daten werden nur für die Lizenzverwaltung verwendet. 
                Weitere Informationen finden Sie in unserer{' '}
                <a href="#" className="text-blue-600 hover:text-blue-800 underline">
                  Datenschutzerklärung
                </a>.
              </p>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-end space-x-3 pt-4">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Abbrechen
            </button>
          )}
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Registrierung läuft...</span>
              </>
            ) : (
              <>
                <Shield className="w-4 h-4" />
                <span>Jetzt registrieren</span>
              </>
            )}
          </button>
        </div>
      </form>

      {/* Support-Info */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="text-center">
          <p className="text-sm text-gray-600 mb-2">
            Benötigen Sie Hilfe bei der Registrierung?
          </p>
          <div className="flex justify-center space-x-4">
            <a
              href="mailto:support@lernrakete.cloud"
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1"
            >
              <Mail className="w-3 h-3" />
              <span>support@lernrakete.cloud</span>
            </a>
            <a
              href="https://docs.lernrakete.cloud"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1"
            >
              <ExternalLink className="w-3 h-3" />
              <span>Dokumentation</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
